import { useCallback, useEffect, useMemo, useState } from 'react';

interface UsePagedSelectionOptions<T> {
  items: T[];
  selectedIds: Set<string>;
  getId: (item: T) => string;
  isSelectable?: (item: T) => boolean;
  pageSize?: number;
}

interface UsePagedSelectionResult<T> {
  displayedItems: T[];
  displayCount: number;
  hasMore: boolean;
  loadMore: () => void;
  allSelected: boolean;
  someSelected: boolean;
}

export function usePagedSelection<T>({
  items,
  selectedIds,
  getId,
  isSelectable = () => true,
  pageSize = 50,
}: UsePagedSelectionOptions<T>): UsePagedSelectionResult<T> {
  const [displayCount, setDisplayCount] = useState(pageSize);

  useEffect(() => {
    setDisplayCount((prev) => Math.min(prev, items.length || pageSize));
  }, [items.length, pageSize]);

  const displayedItems = useMemo(
    () => items.slice(0, displayCount),
    [items, displayCount]
  );

  const hasMore = displayCount < items.length;

  const loadMore = useCallback(() => {
    setDisplayCount((prev) => Math.min(prev + pageSize, items.length));
  }, [items.length, pageSize]);

  const { allSelected, someSelected } = useMemo(() => {
    const selectableItems = items.filter(isSelectable);
    const all =
      selectableItems.length > 0 &&
      selectableItems.every((item) => selectedIds.has(getId(item)));
    const some =
      selectableItems.some((item) => selectedIds.has(getId(item))) && !all;
    return { allSelected: all, someSelected: some };
  }, [items, selectedIds, getId, isSelectable]);

  return {
    displayedItems,
    displayCount,
    hasMore,
    loadMore,
    allSelected,
    someSelected,
  };
}
